import api from './api'

export interface SouvenirProduct {
  id: string
  name: string
  slug: string
  category: string
  description: string
  price: number | string
  compareAtPrice?: number | string
  images: string[]
  inStock: boolean
  quantity: number
  vendor: {
    id: string
    companyName: string
    city?: string
    state?: string
    isVerified: boolean
  }
}

export interface ShippingQuote {
  serviceId: string
  courierName: string
  serviceName: string
  price: number
  eta: string
}

export interface DeliveryAddress {
  name: string
  phone: string
  address: string
  city: string
  state: string
  postalCode: string
}

export interface SouvenirOrder {
  id: string
  orderNumber: string
  subtotal: number | string
  shippingCost: number | string
  platformFee: number | string
  totalAmount: number | string
  deliveryMethod: 'DELIVERY' | 'PICKUP'
  deliveryAddress?: DeliveryAddress
  status: string
  paymentStatus: string
  courierName?: string
  serviceName?: string
  shipmentNumber?: string
  awbNumber?: string
  awbUrl?: string
  trackingUrl?: string
  epStatus?: string
  epStatusHistory?: Array<{ status?: string; label?: string; at?: string; description?: string; date?: string; timestamp?: string }>
  createdAt: string
  items: Array<{
    id: string
    productId: string
    name: string
    quantity: number
    unitPrice: number | string
    subtotal: number | string
    images: string[]
  }>
}

const souvenirService = {
  getProducts: (params?: any) => api.get('/souvenir-products', { params }),
  getProductBySlug: (slug: string) => api.get(`/souvenir-products/slug/${slug}`),

  getShippingQuotes: (items: { productId: string; quantity: number }[], address: { address: string; city: string; state: string; postalCode: string }) =>
    api.post<{ success: boolean; data: ShippingQuote[] }>('/souvenir-orders/shipping-quotes', { items, address }),

  createOrder: (payload: {
    items: { productId: string; quantity: number }[]
    deliveryMethod: 'DELIVERY' | 'PICKUP'
    deliveryAddress?: DeliveryAddress
    courierServiceId?: string
    deliveryNotes?: string
  }) => api.post<{ success: boolean; data: SouvenirOrder }>('/souvenir-orders/create', payload),

  payOrder: (orderId: string) =>
    api.post<{ success: boolean; data: { order: SouvenirOrder; balanceAfter: number } }>(`/souvenir-orders/${orderId}/pay`),

  getMyOrders: (status?: string) =>
    api.get('/souvenir-orders/my-orders', { params: status ? { status } : {} }),

  getVendorOrders: (status?: string) =>
    api.get('/souvenir-orders/vendor-orders', { params: status ? { status } : {} }),

  getOrder: (orderId: string) => api.get(`/souvenir-orders/${orderId}`),

  trackOrder: (orderId: string) =>
    api.get<{ success: boolean; data: { order: SouvenirOrder; epStatusLabel: string | null } }>(`/souvenir-orders/${orderId}/tracking`),

  shipOrder: (orderId: string) =>
    api.post<{ success: boolean; data: SouvenirOrder }>(`/souvenir-orders/${orderId}/ship`),

  updateOrderStatus: (orderId: string, status: string) =>
    api.patch(`/souvenir-orders/${orderId}/status`, { status }),
}

export default souvenirService
